import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle2, ThumbsUp } from 'lucide-react';
import StarRating from './StarRating';

const Outer = styled.div`
  border: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.background};

  > * + * {
    border-top: 1px solid ${(p) => p.theme.colors.border};
  }
`;

const Article = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  padding: clamp(1.15rem, 2vw, 1.55rem);

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    grid-template-columns: 140px 180px 1fr;
    gap: 1.35rem;
  }
`;

const ProductTeaserStatic = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ProductLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
  color: inherit;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }

  &:hover img {
    transform: scale(1.06);
  }
`;

const Thumb = styled.div`
  width: 56px;
  height: 64px;
  flex-shrink: 0;
  overflow: hidden;
  background: ${(p) => p.theme.colors.secondary};

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    width: 80px;
    height: 96px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
  }
`;

const Brand = styled.p`
  margin: 0 0 0.15rem;
  font-size: ${(p) => p.theme.type.bodyXs};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-family: ${(p) => p.theme.fonts.sans};
  color: ${(p) => p.theme.colors.mutedForeground};
`;

const ProductText = styled.div`
  min-width: 0;
`;

const PTitle = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.type.bodySm};
  font-family: ${(p) => p.theme.fonts.sans};
  font-weight: 500;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: ${(p) => p.theme.colors.foreground};

  &:hover {
    text-decoration: underline;
  }
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Author = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.type.bodySm};
  font-family: ${(p) => p.theme.fonts.sans};
  font-weight: 500;
`;

const Mut = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.type.bodyXs};
  color: ${(p) => p.theme.colors.mutedForeground};
  font-family: ${(p) => p.theme.fonts.sans};
`;

const Verified = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0.25rem 0 0;
  font-size: ${(p) => p.theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-family: ${(p) => p.theme.fonts.sans};
  color: hsla(20, 10%, 10%, 0.72);
`;

const ReviewTitle = styled.h4`
  margin: 0 0 0.35rem;
  font-family: ${(p) => p.theme.fonts.sans};
  font-weight: 500;
  font-size: ${(p) => p.theme.type.bodySm};
`;

const BodyText = styled.p`
  margin: 0 0 0.75rem;
  font-size: ${(p) => p.theme.type.bodySm};
  font-family: ${(p) => p.theme.fonts.sans};
  line-height: 1.55;
  color: hsla(20, 10%, 10%, 0.85);
`;

const MetaExtras = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.25rem;
  font-size: ${(p) => p.theme.type.bodyXs};
  color: ${(p) => p.theme.colors.mutedForeground};
  font-family: ${(p) => p.theme.fonts.sans};
  margin-bottom: 0.65rem;
`;

const Upper = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-right: 0.25rem;
`;

const Helpful = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: ${(p) => p.theme.type.bodyXs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-family: ${(p) => p.theme.fonts.sans};
  color: ${(p) => p.theme.colors.mutedForeground};
`;

function parseDate(d) {
  if (!d) return null;
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t;
}

/** Homepage / reviews page table (same API shape as Maison `StorefrontReviewRows`). */
export default function StorefrontReviewRows({ reviews }) {
  if (!reviews?.length) return null;

  return (
    <Outer>
      {reviews.map((r) => {
        const img = r.product?.image || '';
        const pid = r.product?.id ? String(r.product.id) : '';
        const date = parseDate(r.date);
        const productCell = (
          <>
            <Thumb aria-hidden>{img ? <img src={img} alt="" loading="lazy" /> : null}</Thumb>
            <ProductText>
              {r.product?.brand ? <Brand>{r.product.brand}</Brand> : null}
              <PTitle>{r.product?.name || '—'}</PTitle>
            </ProductText>
          </>
        );
        return (
          <Article key={r.id}>
            {pid ? (
              <ProductLink to={`/product/${encodeURIComponent(pid)}`} title={r.product?.name}>
                {productCell}
              </ProductLink>
            ) : (
              <ProductTeaserStatic>{productCell}</ProductTeaserStatic>
            )}

            <MetaCol>
              <Author>{r.author}</Author>
              {r.location ? <Mut>{r.location}</Mut> : null}
              {r.verified ? (
                <Verified>
                  <CheckCircle2 size={14} aria-hidden /> Verified buyer
                </Verified>
              ) : null}
              <Mut style={{ paddingTop: '0.25rem' }}>
                {date
                  ? date.toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'}
              </Mut>
            </MetaCol>

            <div>
              <StarRating rating={Number(r.rating)} size="md" />
              {r.title ? <ReviewTitle>{r.title}</ReviewTitle> : null}
              {r.body ? <BodyText>{r.body}</BodyText> : null}
              {r.size || r.fit || r.recommends ? (
                <MetaExtras>
                  {r.size ? (
                    <span>
                      <Upper>Size:</Upper>
                      {r.size}
                    </span>
                  ) : null}
                  {r.fit ? (
                    <span>
                      <Upper>Fit:</Upper>
                      {r.fit}
                    </span>
                  ) : null}
                  {r.recommends ? <span style={{ color: 'hsla(20,10%,10%,0.72)' }}>✓ Would recommend</span> : null}
                </MetaExtras>
              ) : null}
              <Helpful>
                <ThumbsUp size={14} aria-hidden /> Helpful ({r.helpful ?? 0})
              </Helpful>
            </div>
          </Article>
        );
      })}
    </Outer>
  );
}
